import { PAGES_BY_ID } from './pages'
import {
  PageConditions,
  PageId,
  PagesListContext,
  PagesServiceOptions,
  PageWithEnabled,
  ResolvedPage,
  ResolveOptions,
} from './types'

export type {
  PageCategory,
  PageConditions,
  PageId,
  PageRouteProps,
  PagesListContext,
  PageWithEnabled,
  PagesServiceOptions,
  ResolvedPage,
  ResolveOptions,
} from './types'

export type CrumbSegment = {
  name?: string | null
  link?: string | null
}

type ResolveEnabledProps = {
  bypassPermissions?: boolean
  additionalEnabled?: (conditions?: PageConditions) => boolean
  page: PageWithEnabled
}

const DYNAMIC_SEGMENT_PATTERN = /\{(\w+)\}/g
const ROUTE_PARAM_PATTERN = /^\{.+\}$/

/**
 * Converte a url do `pagesById` para o path do react-router, 1:1.
 * `/farm/{farm}/modules/herd/animals/{animal}` → `/farm/:farm/modules/herd/animals/:animal`
 */
export const toRoutePath = (url: string): string => url?.replace(DYNAMIC_SEGMENT_PATTERN, ':$1')

/**
 * Só as permissões da regra de acesso, sem a assinatura da função — é o que o dev mode mostra.
 * `({ permissions }) => permissions.farm.list_animal` → `permissions.farm.list_animal`
 * Corta no primeiro `=>` para não quebrar caso a regra ganhe uma arrow aninhada.
 */
const toPermissionsSource = (permission: PageWithEnabled['enabled']): string => {
  const [signature, ...body] = permission.toString().split('=>')

  return body.length ? body.join('=>').trim() : signature.trim()
}

const isSearchable = (url: string, context: PagesListContext): boolean => {
  // Telas fora de tenant (logout, manutenção, 404) nunca são destino de busca.
  if (context === 'guest') return false

  const dynamicSegments = url.match(DYNAMIC_SEGMENT_PATTERN) ?? []
  if (context === 'system') return dynamicSegments.length === 0

  return dynamicSegments.every(segment => segment === '{farm}')
}

let cachedPathPatternById: ReadonlyMap<string, RegExp> | null = null

// Adiada e cacheada pelo mesmo motivo de `PAGES_BY_ID()`: não pode rodar no top-level do
// módulo, antes do boot (`Translate.setContent()`), então só é construída no primeiro uso.
const getPathPatternById = (): ReadonlyMap<string, RegExp> => {
  if (!cachedPathPatternById) {
    cachedPathPatternById = new Map(
      Object.entries(PAGES_BY_ID()).map(([id, page]) => [
        id,
        new RegExp(`^${page.url.replace(DYNAMIC_SEGMENT_PATTERN, '[^/]+')}/?$`),
      ]),
    )
  }
  return cachedPathPatternById
}

export class PagesService {
  private readonly conditions: PageConditions | undefined
  private readonly params: Record<string, string>
  private readonly onPermissionError: (error: unknown, page: PageWithEnabled) => void

  /**
   * `conditions` só é lido quando uma permissão é de fato avaliada — chamadas com
   * `bypassPermissions: true` (ex.: `getPlaywrightPages`) nunca o tocam, então é opcional.
   */
  public constructor(conditions?: PageConditions, options?: PagesServiceOptions) {
    this.conditions = conditions
    this.params = options?.params ?? {}
    this.onPermissionError = options?.onPermissionError ?? console.error
  }

  public getAll(): ResolvedPage[] {
    return Object.entries(PAGES_BY_ID()).map(([id, page]) => this.resolve(id as PageId, page))
  }

  public getAvailable(options?: {
    context?: PagesListContext
    bypassPermissions?: boolean
    params?: Record<string, string>
  }): ResolvedPage[] {
    return Object.entries(PAGES_BY_ID())
      .map(([id, page]) =>
        this.resolve(id as PageId, page, {
          bypassPermissions: options?.bypassPermissions,
          params: options?.params,
        }),
      )
      .filter(resolvedPage => resolvedPage.enabled)
      .filter(resolvedPage => (options?.context ? resolvedPage.context === options.context : true))
  }

  /**
   * Todas as páginas com o link já resolvido a partir de `params` (ex.: `config.params` do
   * Playwright), ignorando `enabled` — equivalente ao antigo `createLink`/`getPages` de
   * `playwright/src/pages.ts`, porém a partir da mesma fonte usada pelo app.
   */
  public getPlaywrightPages(
    params: Record<string, string>,
    options?: { context?: PagesListContext },
  ): ResolvedPage[] {
    return this.getAvailable({ ...options, bypassPermissions: true, params })
  }

  public getById(id: PageId, options?: ResolveOptions): ResolvedPage | undefined {
    const page = PAGES_BY_ID()[id as PageId]
    if (!page) return undefined

    return this.resolve(id, page, options)
  }

  /**
   * Apenas a permissão de acesso da página — `false` quando o id não existe. Evita resolver
   * link/crumbs quando só interessa saber se o usuário pode acessar (ex.: `enabled` de ações).
   */
  public getEnabledById(id: string, options?: ResolveOptions): boolean {
    const page = PAGES_BY_ID()[id as PageId]
    if (!page) return false

    const enabled = this.resolveEnabled({
      additionalEnabled: options?.additionalEnabled,
      bypassPermissions: options?.bypassPermissions,
      page,
    })

    return enabled
  }

  public getCurrent(options?: ResolveOptions & { path?: string }): ResolvedPage | undefined {
    const path = options?.path ?? window.location.pathname

    for (const [id, pattern] of getPathPatternById()) {
      if (pattern.test(path))
        return this.resolve(id as PageId, PAGES_BY_ID()[id as PageId], options)
    }

    return undefined
  }

  /**
   * Resolve a trilha de `crumbs` (+ título) de uma página em segmentos com título/link.
   * Usado tanto pelo `crumb` (string) de `resolve()` quanto pelo PageHeader, que monta a
   * trilha clicável passando um `crumbMap` para substituir os parâmetros de rota (Ex.:
   * `{farm}`) pelo nome real da entidade.
   */
  public extractCrumbs(options: {
    page?: Pick<PageWithEnabled, 'crumbs' | 'title'>
    crumbMap?: Record<string, CrumbSegment>
    bypassPermissions?: boolean
  }): CrumbSegment[] {
    const { bypassPermissions, crumbMap, page } = options
    if (!page) return []

    const segments = [...page.crumbs, page.title]

    return segments
      .map(segment => {
        if (!segment) return

        if (ROUTE_PARAM_PATTERN.test(segment)) return crumbMap?.[segment]

        const foundPage = this.getById(segment as PageId, { bypassPermissions })
        if (!foundPage) return { name: segment }

        return { link: foundPage.enabled ? foundPage.link : null, name: foundPage.title }
      })
      .filter(Boolean) as CrumbSegment[]
  }

  private resolve(id: PageId, page: PageWithEnabled, options?: ResolveOptions): ResolvedPage {
    // O que sobra de `options` são as props de rota (`component`, `exact`, `protected`), que
    // apenas atravessam o resolve para chegar no objeto devolvido.
    const { additionalEnabled, bypassPermissions, params, ...routeProps } = options ?? {}

    const enabled = this.resolveEnabled({ additionalEnabled, bypassPermissions, page })

    const mergedParams = { ...this.params, ...params }
    const link = page.url.replace(
      DYNAMIC_SEGMENT_PATTERN,
      (_, key: string) => mergedParams[key] ?? `{${key}}`,
    )
    const crumb = this.extractCrumbs({ bypassPermissions, page })
      .map(segment => segment.name)
      .filter(Boolean)
      .join(' / ')
    const searchable = isSearchable(page.url, page.context)

    return {
      ...page,
      ...routeProps,
      crumb,
      enabled,
      id,
      link,
      permissions: toPermissionsSource(page.enabled),
      searchable,
    }
  }

  private resolveEnabled({ additionalEnabled, bypassPermissions, page }: ResolveEnabledProps) {
    if (bypassPermissions) return true
    if (!page.enabled) return true

    try {
      /**
       * `conditions` só é opcional para o caminho bypass (que retorna antes de chegar aqui);
       * quando chega, é esperado que conditions reais tenham sido passadas.
       */
      const conditions = this.conditions as PageConditions

      return Boolean(page.enabled(conditions) && (additionalEnabled?.(conditions) ?? true))
    } catch (error) {
      this.onPermissionError(error, page)
      return false
    }
  }
}
